import React, { useMemo, useState } from "react";
import {
  CheckCircleFilled,
  EyeInvisibleOutlined,
  EyeOutlined,
  LoadingOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import adminApi from "../api/adminApi";

const initialForm = { currentPassword: "", newPassword: "", confirmPassword: "" };

export default function SecuritySettings() {
  const [form, setForm] = useState(initialForm);
  const [visible, setVisible] = useState({ current: false, next: false, confirm: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const checks = useMemo(() => [
    { label: "At least 8 characters", met: form.newPassword.length >= 8 },
    { label: "Passwords match", met: Boolean(form.confirmPassword) && form.newPassword === form.confirmPassword },
    { label: "Different from current password", met: Boolean(form.newPassword) && form.newPassword !== form.currentPassword },
  ], [form]);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setError("");
    setSuccess("");
  }

  function toggle(field) {
    setVisible((current) => ({ ...current, [field]: !current[field] }));
  }

  async function submit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (form.newPassword.length < 8) return setError("Your new password must contain at least 8 characters.");
    if (form.newPassword !== form.confirmPassword) return setError("The new password and confirmation do not match.");
    if (form.currentPassword === form.newPassword) return setError("Choose a new password that is different from your current password.");

    setSaving(true);
    try {
      const response = await adminApi.resetPassword(form.currentPassword, form.newPassword);
      setForm(initialForm);
      setSuccess(response.message || "Password reset successfully. Your secure session has been renewed.");
    } catch (err) {
      setError(adminApi.getErrorMessage(err, "Password could not be changed. Please verify your current password."));
    } finally {
      setSaving(false);
    }
  }

  const ready = form.currentPassword && checks.every((check) => check.met);

  return (
    <div className="admin-security-layout">
      <section className="admin-panel admin-security-card">
        <div className="admin-security-heading">
          <div><LockOutlined /></div>
          <span className="admin-eyebrow">Account protection</span>
          <h2>Change your password</h2>
          <p>Confirm your current password, then choose a secure replacement. Your active session will be renewed automatically.</p>
        </div>

        {error && <div className="admin-alert error" role="alert">{error}</div>}
        {success && <div className="admin-alert success" role="status"><CheckCircleFilled /> <span>{success}</span></div>}

        <form onSubmit={submit} className="admin-security-form">
          <PasswordField label="Current password" value={form.currentPassword} visible={visible.current} onChange={(value) => update("currentPassword", value)} onToggle={() => toggle("current")} autoComplete="current-password" />
          <div className="admin-password-divider" />
          <PasswordField label="New password" value={form.newPassword} visible={visible.next} onChange={(value) => update("newPassword", value)} onToggle={() => toggle("next")} autoComplete="new-password" hint="Use at least 8 characters." />
          <PasswordField label="Confirm new password" value={form.confirmPassword} visible={visible.confirm} onChange={(value) => update("confirmPassword", value)} onToggle={() => toggle("confirm")} autoComplete="new-password" />

          <div className="admin-password-checks">
            {checks.map((check) => <span className={check.met ? "met" : ""} key={check.label}><CheckCircleFilled /> {check.label}</span>)}
          </div>
          <button className="admin-primary-button admin-password-submit" disabled={!ready || saving}>
            {saving ? <><LoadingOutlined spin /> Updating password…</> : <><SafetyCertificateOutlined /> Update password</>}
          </button>
        </form>
      </section>

      <aside className="admin-security-note">
        <SafetyCertificateOutlined />
        <div><strong>Security reminder</strong><p>Use a password you do not use elsewhere. After a successful change, your old access and refresh tokens are replaced with new ones.</p></div>
      </aside>
    </div>
  );
}

function PasswordField({ label, value, visible, onChange, onToggle, autoComplete, hint }) {
  return (
    <label className="admin-field admin-password-field">
      <span>{label} <b>*</b></span>
      <div>
        <LockOutlined />
        <input type={visible ? "text" : "password"} value={value} onChange={(event) => onChange(event.target.value)} autoComplete={autoComplete} required placeholder={`Enter ${label.toLowerCase()}`} />
        <button type="button" onClick={onToggle} aria-label={visible ? `Hide ${label}` : `Show ${label}`}>{visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}</button>
      </div>
      {hint && <small>{hint}</small>}
    </label>
  );
}
